// user-list.component.ts

import { Component, OnInit, ChangeDetectorRef, HostListener, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterConfigType, TableColumn, TableComponent } from '@shared/components/table/table.component';
import { SharedModule } from '@shared/shared_module';
import { ApiService, OnboardedUser, Region, UserRole, Country } from '@core/services/api/api.service';
import { LazyLoadEvent } from 'primeng/api';
import { LoaderService } from '@core/services/gigya/loader/loader.service';
import { finalize } from 'rxjs/operators';
import { UserFormComponent } from '../create-user/create-new-user/user-form.component';
import { DialogModule } from 'primeng/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { CertificateCellComponent } from '../user-requests/certificate-cell/certificate-cell/certificate-cell.component';
import { UserEventsService } from '@core/services/events/user-events.service';
import { Subscription } from 'rxjs';


@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    SharedModule,
    UserFormComponent,
    DialogModule
],
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  providers: [DatePipe]
})
export class UserListComponent implements OnInit, OnDestroy {
  userData: OnboardedUser[] = [];
  userColumns: TableColumn[] = [];
  userGlobalFilterFields: string[] = [];
  userFiltersConfig: FilterConfigType[] = [];
  userFiltersValue: { [key: string]: any } = {};

  userActions: any[] = [];

  userTotalRecords: number = 0;
  userLoading: boolean = false;
  userPage: number = 1;
  userPageSize: number = 10;
  userLastLazyLoadEvent: LazyLoadEvent | null = null;

  filterRegions: Region[] = [];
  filterUserRoles: UserRole[] = [];
  filterCountries: Country[] = [];

  showUserFormDialog: boolean = false;
  isEditMode: boolean = false;
  selectedUserForEdit: OnboardedUser | null = null;

  // Properties for PDF viewer
  displayPdfViewer: boolean = false;
  pdfUrl: string | null = null;
  pdfFileName: string = 'Training Certificate';
  safePdfUrl: any = null; // SafeResourceUrl for iframe

  // Click popup state for +N
  listPopupVisible = false;
  listPopupTitle = 'Items';
  listPopupItems: string[] = [];
  listPopupX = 0;
  listPopupY = 0;
  queryParamFilter: { label: string; value: string }[] = [];

  private docClickCapture?: (e: MouseEvent) => void;
  private userCreatedSub?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly apiService: ApiService,
    private readonly datePipe: DatePipe,
    private readonly loaderService: LoaderService,
    private readonly sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private readonly userEvents: UserEventsService
  ) {}

  ngOnInit(): void {
    this.fetchFilterOptions();
    this.setUsersTabConfig();
    this.initializeFilterValues(this.userFiltersConfig, this.userFiltersValue);

    // Subscribe to global user created events to update list
    this.userCreatedSub = this.userEvents.created$.subscribe((created) => {
      if (!created) return;
      // Avoid duplicating if already present
      if (this.userData.find(u => (u as any).id === (created as any).id)) return;

      const row = this.transformApiUserToRow(created);
      if (this.userPage === 1) {
        this.userData = [row, ...(this.userData || [])].slice(0, this.userPageSize);
        for (let i = 0; i < this.userData.length; i++) (this.userData[i] as any).sno = i + 1;
      }
      this.userTotalRecords = (this.userTotalRecords || 0) + 1;
      this.cdr.detectChanges();
    });

    this.route.queryParams.subscribe((queryParams) => {
      if (queryParams["value"]) {
        // If queryParams present, call filtered API logic
        this.apiService.getUserRoles().subscribe((response) => {
          const roles = response.user_roles;
          if (queryParams["value"] === "inactive") {
            this.queryParamFilter = [
              { label: "Inactive", value: "deactivated" },
            ];
            this.onUserSearchClicked(
              this.queryParamFilter.length
                ? { statuses: this.queryParamFilter }
                : {}
            );
          } else {
            this.queryParamFilter = this.getRolesByType(
              roles,
              queryParams["value"]
            ).map((r) => ({
              label: r.name,
              value: String(r.id),
            }));
            this.onUserSearchClicked(
              this.queryParamFilter.length
                ? { role_id: this.queryParamFilter }
                : {}
            );
          }
        });
      } else {
        // If no queryParams, call default API
        this.loadOnboardedUsers({ first: 0, rows: this.userPageSize });
      }
    });

    // Ensure we capture clicks even if propagation is stopped inside table internals
    // this.docClickCapture = (ev: MouseEvent) => this.handleMoreBadgeClick(ev);
    // document.addEventListener('click', this.docClickCapture, true);
  }

  ngOnDestroy(): void {
    if (this.docClickCapture) {
      document.removeEventListener('click', this.docClickCapture, true);
      this.docClickCapture = undefined;
    }
    this.userCreatedSub?.unsubscribe();
  }
  getRolesByType(roles: UserRole[], type: string) {
    return roles.filter((r) =>
      r.name.toLowerCase().includes(type.toLowerCase())
    );
  }

  initializeFilterValues(config: FilterConfigType[], values: { [key: string]: any }) {
    config.forEach(f => {
      if (f.type === 'multiselect') {
        values[f.key] = [];
      } else if (f.type === 'daterange') {
        values[f.key] = { from: null, to: null };
      } else {
        values[f.key] = null;
      }
    });
  }

  onLazyLoadEventFromTable(event: LazyLoadEvent): void {
    this.userLastLazyLoadEvent = event;
    this.loadOnboardedUsers(event);
  }

  onUserSearchClicked(filters: { [key: string]: any }) {
    this.userFiltersValue = { ...filters };

    const currentGlobalFilter = this.userLastLazyLoadEvent?.globalFilter;
    this.userLastLazyLoadEvent = {
      first: 0,
      rows: this.userPageSize,
      globalFilter: currentGlobalFilter
    };
    this.loadOnboardedUsers(this.userLastLazyLoadEvent);
  }


  fetchFilterOptions(): void {
    this.apiService.getRegions().subscribe({
      next: (response) => {
        this.filterRegions = response.regions;
        this.updateUserFilterOptions();
      },
      error: (err) => console.error('Failed to load filter regions:', err)
    });
    this.apiService.getUserRoles().subscribe({
      next: (response) => {
        this.filterUserRoles = response.user_roles;
        this.updateUserFilterOptions();
      },
      error: (err) => console.error('Failed to load filter user roles:', err)
    });
    this.apiService.getCountries().subscribe({
      next: (response) => {
        this.filterCountries = response.countries;
        this.updateUserFilterOptions();
      },
      error: (err) => console.error('Failed to load filter countries:', err)
    });
  }

  updateUserFilterOptions(): void {
    this.userFiltersConfig = [
      {
        key: "role_id",
        label: "Role",
        type: "multiselect",
        options: this.filterUserRoles.map(r => ({ label: r.name, value: String(r.id) })),
      },
      {
        key: "region_id",
        label: "Assigned Region",
        type: "multiselect",
        options: this.filterRegions.map(r => ({ label: r.name, value: String(r.id) })),
      },

      {
        key: "responsible_country_ids",
        label: "Responsible Countries",
        type: "multiselect",
        options: this.filterCountries.map(c => ({ label: c.name, value: String(c.id) })),
      },
      {
        key: "statuses",
        label: "Status",
        type: "multiselect",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "deactivated" },
        ],
      },
      {
        key: "added_at",
        label: "Added Date Range",
        type: "daterange",
      },
    ];
    this.cdr.detectChanges();
  }

  setUsersTabConfig(): void {
    this.userColumns = [
      { field: "sno", header: "S.No", sortable: false, width: "2rem" },
      { field: "first_name", header: "First Name", sortable: false },
      { field: "last_name", header: "Last Name", sortable: false },
      { field: "email", header: "Email Id", sortable: false, filterable: false, truncateAt: 20 },
      { field: "role_name", header: "Role Type", sortable: false, truncateAt: 20 },
      { field: "country_name", header: "Country", sortable: false },
      { field: "region_name", header: "User Responsible Region", sortable: false },
      {
        field: "formatted_user_responsible_country",
        header: "User Responsible Country",
        sortable: false,
        isCustom: true,
        customTemplate: (rowData: any) => {
          const first = rowData.responsible_country_first || 'N/A';
          const remain = Number(rowData.responsible_country_remaining || 0);
          const others: string[] = Array.isArray(rowData.responsible_country_remaining_names) ? rowData.responsible_country_remaining_names : [];
          const encoded = encodeURIComponent(JSON.stringify(others));

          return  `  <div class="cell-multi">
            <span class="primary-text">${this.escapeHtml(String(first))}</span>
            ${remain ? `<span role="button" tabindex="0" class="more-badge white-outline country-count-badge--outlined">+${remain}</span><span class="more-items" style="display:none">${JSON.stringify(
                  others
                )}</span>` : ''}
          </div>`;
        }
      },
      { field: "company_name", header: "Company Name", sortable: false },
      { field: "formatted_start_date", header: "Start Date", sortable: false },
      {
        field: "certificate_meta",
        header: "Certificate",
        sortable: false,
        customCellComponent: CertificateCellComponent
      },
      {
        field: "status",
        header: "Status",
        sortable: false,
        isCustom: true,
        width: "7rem",
        customTemplate: (rowData: any) => {
          const status = String(rowData.status || '').toLowerCase();
          const cls = status.includes('inactive') || status.includes('deactivated') ? 'status-deactivated' : 'status-active';
          const label = rowData.status || '';
          return `<div class="status-pill ${cls}"><span class="status-text">${label}</span></div>`;
        }
      },
      { field: "actions", header: "Actions", sortable: false, isAction: true },
    ];

    this.userGlobalFilterFields = [
      "first_name", "last_name", "email", "company_name", "status"
    ];

    this.userActions = [
      {
        label: "Edit",
        class: "edit-button",
        onClick: (row: any) => this.editUser(row),
      },
    ];
  }

  getRoleName(roleId: number): string {
    const role = this.filterUserRoles.find(r => r.id === roleId);
    return role ? role.name : 'N/A';
  }

  getCountryName(countryId: number): string {
    const country = this.filterCountries.find(c => c.id === countryId);
    return country ? country.name : 'N/A';
  }

  getRegionName(regionId: number): string {
    const region = this.filterRegions.find(r => r.id === regionId);
    return region ? region.name : 'N/A';
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return this.datePipe.transform(dateString, 'dd/MM/yyyy') || 'N/A';
  }

  private buildCertificateMeta(certificateInfo: any, fallbackUrl: string | null | undefined) {
    const info = certificateInfo ?? {};
    const url = info?.url || fallbackUrl || null;

    if (!url && !info?.status) {
      return null;
    }

    return {
      ...info,
      url,
      file_name: info?.file_name || (url ? this.extractFileNameFromUrl(url) : null),
    };
  }

  private extractFileNameFromUrl(url: string): string {
    if (!url) {
      return 'certificate.pdf';
    }
    return url.substring(url.lastIndexOf('/') + 1).split('?')[0] || 'certificate.pdf';
  }

  formatResponsibleCountries(countryIds: number[] | undefined): string {
    if (!countryIds || countryIds.length === 0) {
      return 'N/A';
    }

    const countryNames = countryIds.map(id => this.getCountryName(id)).filter(name => name !== 'N/A');

    if (countryNames.length === 0) {
      return 'N/A';
    }

    const firstCountry = countryNames[0];
    const remainingCount = countryNames.length - 1;

    if (remainingCount > 0) {
      return `${firstCountry} <span class="country-count-badge">+${remainingCount}</span>`;
    } else {
      return firstCountry;
    }
  }

  loadOnboardedUsers(event?: LazyLoadEvent): void {
    this.userLoading = true;
    this.loaderService.show();

    const currentEvent = event || this.userLastLazyLoadEvent || { first: 0, rows: this.userPageSize };

    this.userPage = (currentEvent.first ?? 0) / (currentEvent.rows ?? this.userPageSize) + 1;
    this.userPageSize = currentEvent.rows ?? this.userPageSize;
    const filtersToSend: { [key: string]: any } = {};
    for (const key in this.userFiltersValue) {
      if (Object.hasOwn(this.userFiltersValue, key)) {
        let value = this.userFiltersValue[key];
        const filterConfig = this.userFiltersConfig.find(f => f.key === key);

        if (filterConfig && value) {
          if (filterConfig.type === 'multiselect' && Array.isArray(value) && value.length > 0) {
            const primitiveValues = value.map(item => item.value).filter(v => v !== null && v !== undefined);
            if (primitiveValues.length > 0) {
              filtersToSend[key] = primitiveValues.join(',');
            }
          }
          // Handle daterange type - check for both array format and object format
          else if (filterConfig.type === 'daterange') {            
            // Handle array format [fromDate, toDate]
            if (Array.isArray(value) && value.length >= 2) {
              const fromDate = value[0];
              const toDate = value[1];
              
              if (fromDate) {
                const fromDateFormatted = this.datePipe.transform(fromDate, 'yyyy-MM-dd');
                if (fromDateFormatted) {
                  filtersToSend[`${key}_from`] = fromDateFormatted;
                }
              }
              
              if (toDate) {
                const toDateFormatted = this.datePipe.transform(toDate, 'yyyy-MM-dd');
                if (toDateFormatted) {
                  filtersToSend[`${key}_to`] = toDateFormatted;
                }
              }
            }
            // Handle object format {from: Date, to: Date}
            else if (typeof value === 'object' && value !== null && (value.from || value.to)) {
              if (value.from) {
                const fromDate = this.datePipe.transform(value.from, 'yyyy-MM-dd');
                if (fromDate) {
                  filtersToSend[`${key}_from`] = fromDate;
                }
              }
              if (value.to) {
                const toDate = this.datePipe.transform(value.to, 'yyyy-MM-dd');
                if (toDate) {
                  filtersToSend[`${key}_to`] = toDate;
                }
              }
            }
          } else if (filterConfig.type === 'text' || filterConfig.type === 'dropdown') {
             if (value !== null && value !== undefined && value !== '') {
                const finalValue = (typeof value === 'object' && value.hasOwnProperty('value')) ? value.value : value;
                if (finalValue !== null && finalValue !== undefined && finalValue !== '') {
                filtersToSend[key] = finalValue;
              }
            }
          }
        }
      }
    }

    if (currentEvent.globalFilter && currentEvent.globalFilter.trim() !== '') {
      filtersToSend['search'] = currentEvent.globalFilter.trim();
    }

    this.apiService.getOnboardedUsers(this.userPage, this.userPageSize, filtersToSend).pipe(
        finalize(() => {
          this.userLoading = false;
          this.loaderService.hide();
        })
    ).subscribe({
        next: (response) => {
          this.userData = response.users.map((user, index) => {
          const assignedIds: number[] = ((user as any).assigned_country_ids as number[]) || [];
            const assignedNames: string[] = assignedIds
              .map((id: number) => this.getCountryName(id))
            .filter((n: string) => !!n && n !== 'N/A');
          const firstName: string = assignedNames[0] || 'N/A';
          const remainingCount: number = Math.max(0, assignedNames.length - 1);
          const remainingNames: string[] = remainingCount > 0 ? assignedNames.slice(1) : [];
          const certificateMeta = this.buildCertificateMeta(user.certificate_info, user.certificate_url);

          return ({
              ...user,
              sno: (this.userPage - 1) * this.userPageSize + index + 1,
            certificate_meta: certificateMeta,
            certificate_url: certificateMeta?.url || null,
              role_name: this.getRoleName(user.role_id),
              country_name: this.getCountryName(user.country_id),
              region_name: this.getRegionName((user as any).assigned_region_id),
              formatted_user_responsible_country: firstName,
              responsible_country_first: firstName,
              responsible_country_remaining: remainingCount,
              responsible_country_remaining_names: remainingNames,
              formatted_start_date: this.formatDate(user.created_at),
          });
          });
          this.userTotalRecords = response.meta.total;
          this.cdr.detectChanges();
        },
        error: (error) => {
        console.error('Failed to load onboarded users:', error);
          this.userData = [];
          this.userTotalRecords = 0;
      }
      });
  }

  editUser(user: OnboardedUser): void {
    this.isEditMode = true;
    this.selectedUserForEdit = user;
    this.showUserFormDialog = true;
  }

  openCreateUserDialog(): void {
    this.isEditMode = false;
    this.selectedUserForEdit = null;
    this.showUserFormDialog = true;
  }

  onUserFormClosed(): void {
    this.showUserFormDialog = false;
    this.isEditMode = false;
    this.selectedUserForEdit = null;
  }

  onUserCreated(event: any): void {
    const created: OnboardedUser = event?.createdUser || event;
    if (!created) {
      this.loadOnboardedUsers(this.userLastLazyLoadEvent || { first: 0, rows: this.userPageSize });
      return;
    }

    // Map API user -> table row
    const row = this.transformApiUserToRow(created);

    // If current view is first page, prepend the new user; otherwise, just increase total count
    if (this.userPage === 1) {
      // Insert and trim to page size
      this.userData = [row, ...(this.userData || [])].slice(0, this.userPageSize);
      // Recalculate sno for displayed rows
      for (let i = 0; i < this.userData.length; i++) {
        (this.userData[i] as any).sno = i + 1;
      }
    }

    this.userTotalRecords = (this.userTotalRecords || 0) + 1;
    this.cdr.detectChanges();
  }

  onUserUpdated(event: any): void {
    this.loadOnboardedUsers(this.userLastLazyLoadEvent!);
  }

  onViewCertificate(event: any): void {
    const incomingUrl: string = typeof event === 'string' ? event : event?.url;
    const incomingName: string = event?.fileName || 'Training Certificate';

    if (!incomingUrl) {
      console.warn('No certificate URL provided');
      return;
    }

    const proceedPreview = (url: string) => {
      this.pdfUrl = url;
      try {
        this.pdfFileName = decodeURIComponent(incomingName);
      } catch {
        this.pdfFileName = incomingName;
      }
      this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.displayPdfViewer = true;
      this.cdr.detectChanges();
    };

    const isAttachment = incomingUrl.includes('/attachments/download/');
    if (!isAttachment) {
      proceedPreview(incomingUrl);
      return;
    }

    this.apiService.getSignedAttachmentDownloadFromUrl(incomingUrl).subscribe({
      next: (res) => proceedPreview(res.download_url),
      error: (err) => {
        console.error('Failed to get signed certificate URL', err);
      }
    });
  }

  // Unified handler for +N clicks
 @HostListener("document:click", ["$event"])
  onDocumentClick(ev: MouseEvent): void {
    const target = ev.target as EventTarget | null;
    const badgeEl = target && (target as Element).closest ? (target as Element).closest(".more-badge") : null;
    if (badgeEl instanceof HTMLElement) {
      const container = badgeEl.closest(".cell-multi");
      const hidden = container?.querySelector(".more-items");
      let list: string[] = [];
      const rawText = hidden?.textContent?.trim();
      if (rawText) {
        try {
          list = JSON.parse(rawText);
        } catch {}
      }
      // if (!list.length) return;
      const titleEl = badgeEl.getAttribute("data-title");
      const title = titleEl || "Countries";
      const rect = badgeEl.getBoundingClientRect();
      this.openListPopup({ title, items: list, x: rect.left, y: rect.bottom });
      return;
    }

    // Close when clicking outside the popup
    const popup = document.querySelector(".list-popup");
    if (popup && target instanceof Node && !popup.contains(target)) {
      this.closeListPopup();
    }
  }
 
  openListPopup(payload: { title: string; items: string[]; x?: number; y?: number }): void {
    this.listPopupTitle = payload.title;
    this.listPopupItems = payload.items;
    this.listPopupX = Math.max(12, Math.min((payload.x ?? 0), window.innerWidth - 280));
    this.listPopupY = Math.max(12, Math.min((payload.y ?? 0), window.innerHeight - 280));
    this.listPopupVisible = true;
  }

  closeListPopup(): void { this.listPopupVisible = false; }

  private escapeHtml(v: string): string {
    return v.replace(/[&<>"']/g, (c) => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' } as any)[c]);
  }

  private transformApiUserToRow(user: OnboardedUser): OnboardedUser & any {
    const assignedIds: number[] = ((user as any).assigned_country_ids as number[]) || [];
    const assignedNames: string[] = assignedIds
      .map((id: number) => this.getCountryName(id))
      .filter((n: string) => !!n && n !== 'N/A');
    const firstName: string = assignedNames[0] || 'N/A';
    const remainingCount: number = Math.max(0, assignedNames.length - 1);
    const remainingNames: string[] = remainingCount > 0 ? assignedNames.slice(1) : [];
    const certificateMeta = this.buildCertificateMeta(user.certificate_info, user.certificate_url);

    return ({
      ...user,
      sno: 1,
      certificate_meta: certificateMeta,
      certificate_url: certificateMeta?.url || null,
      role_name: this.getRoleName(user.role_id),
      country_name: this.getCountryName(user.country_id),
      region_name: this.getRegionName((user as any).assigned_region_id),
      formatted_user_responsible_country: firstName,
      responsible_country_first: firstName,
      responsible_country_remaining: remainingCount,
      responsible_country_remaining_names: remainingNames,
      formatted_start_date: this.formatDate(user.created_at),
    });
  }
}